import dedent from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemasParams,
  Language,
  Provider,
  QueryAndOutputs,
  TableSchemasList,
  WorkflowComponentCodeParams,
  WorkflowComponentOutputTablesParams,
  WorkflowComponentOutputType,
  WorkflowComponentParamType
} from '../types'
import { DBricksConnection } from '../workflow/connections/databricks'
import { WorkflowComponent } from './common/workflowcomponent'

const predicates = new Map([
  ['intersects', 'ST_INTERSECTS'],
  ['contains', 'ST_CONTAINS'],
  ['within', 'SEDONA_ST_WITHIN'],
  ['covers', 'SEDONA_ST_COVERS']
])

export const FILTER_TABLE_TRUE_NAME = 'match'
export const FILTER_TABLE_FALSE_NAME = 'unmatch'

class SpatialFilterDB extends WorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.spatialfilter',
        group: 'Data Preparation',
        title: 'Spatial Filter',
        description: `
      This component filters an input table using a spatial predicate and a filter table.
      `,
        starred: true
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'filter',
          type: WorkflowComponentParamType.InputTable,
          title: 'Filter table'
        },
        {
          name: 'geosource',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column in source table',
          parent: 'source',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'geofilter',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column in filter table',
          parent: 'filter',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'predicate',
          type: WorkflowComponentParamType.Selection,
          title: 'Spatial predicate',
          default: predicates.keys().next().value,
          optional: true,
          options: Array.from(predicates.keys())
        }
      ],
      [
        {
          name: FILTER_TABLE_TRUE_NAME,
          title: 'Matched',
          description: 'Table with rows that pass the filter',
          type: WorkflowComponentOutputType.Table
        },
        {
          name: FILTER_TABLE_FALSE_NAME,
          title: 'Unmatched',
          description: 'Table with rows that do not pass the filter',
          type: WorkflowComponentOutputType.Table
        }
      ]
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    const tablenameMatch = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      FILTER_TABLE_TRUE_NAME,
      codeType
    )
    const tablenameUnmatch = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      FILTER_TABLE_FALSE_NAME,
      codeType
    )
    const preparedParams = this._prepareParameters(params)

    const outputTablename = connection.getOutputTableName(workflow.id, componentName, params, 'result', codeType)

    const leftOutputTableName = connection.quoteName(outputTablename + '_left')
    const rightOutputTableName = connection.quoteName(outputTablename + '_right')

    const match = await this._getMatchCode(preparedParams, leftOutputTableName, rightOutputTableName)
    const unmatch = await this._getUnmatchCode(preparedParams, connection as DBricksConnection, tablenameMatch)
    if (match === null || unmatch === null) {
      return null
    }
    const queryMatch = (connection as DBricksConnection).wrapSQLCodeAsPythonCall(
      connection.getCreateTempTableQuery(tablenameMatch, match, codeType, null)
    )

    const unmatchIsUsed =
      workflow && componentName && codeType ? workflow.isTableUsed(componentName, FILTER_TABLE_FALSE_NAME) : true
    const queryUnmatch = unmatchIsUsed
      ? (connection as DBricksConnection).wrapSQLCodeAsPythonCall(
          connection.getCreateTempTableQuery(tablenameUnmatch, unmatch, codeType, null)
        )
      : ''

    const generateSpatialFilterTablesQuery = (connection as DBricksConnection).generateSpatialFilterTables(
      connection.quoteName(preparedParams.source as string),
      connection.quoteName(preparedParams.filter as string),
      preparedParams.geosource as string,
      preparedParams.geofilter as string,
      leftOutputTableName,
      rightOutputTableName
    )
    const query = `${generateSpatialFilterTablesQuery}\n${queryMatch}\n${queryUnmatch}`

    const sqlret: QueryAndOutputs = { query: query, outputs: {} }
    sqlret.outputs[FILTER_TABLE_TRUE_NAME] = tablenameMatch
    sqlret.outputs[FILTER_TABLE_FALSE_NAME] = tablenameUnmatch

    return sqlret
  }

  getOutputTables({
    workflow,
    params,
    connection,
    componentName,
    codeType
  }: WorkflowComponentOutputTablesParams): Record<string, string> {
    const tablenameMatch = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      FILTER_TABLE_TRUE_NAME,
      codeType
    )
    const tablenameUnmatch = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      FILTER_TABLE_FALSE_NAME,
      codeType
    )
    const outputs = {}
    outputs[FILTER_TABLE_TRUE_NAME] = tablenameMatch
    outputs[FILTER_TABLE_FALSE_NAME] = tablenameUnmatch
    return outputs
  }

  async _getUnmatchCode(params: Record<string, unknown>, connection: DBricksConnection, tablenameMatch: string) {
    tablenameMatch = connection.quoteName(tablenameMatch)
    return dedent`
    SELECT
      __source.*
    FROM
      ${connection.quoteName(params.source as string)} __source
    WHERE ${params.geosource} NOT IN
      (
        SELECT ${params.geosource}
        FROM ${tablenameMatch}
      )`
  }

  async _getMatchCode(params: Record<string, unknown>, leftOutputTableName: string, rightOutputTableName: string) {
    let geomFromWKBFunct = 'ST_GEOMFROMWKB'
    if (params.predicate === 'within' || params.predicate === 'covers') {
      geomFromWKBFunct = 'SEDONA_ST_GEOMFROMWKB'
    }
    return dedent`WITH _main AS (
        SELECT /*+ REPARTITION(40, ${params.geosource}) */
        *, ${geomFromWKBFunct}(${params.geosource}) AS ${params.geosource}_processed FROM ${leftOutputTableName}
      ),
      _joined AS (
        SELECT /*+ REPARTITION(40, ${params.geofilter}) */
        *, ${geomFromWKBFunct}(${params.geofilter}) AS ${params.geofilter}_processed FROM ${rightOutputTableName}
      )
      SELECT /*+ REPARTITION(40) */
        _main.* EXCEPT(${
          params.geosource
        }_processed, __carto_bounds_xmin, __carto_bounds_xmax, __carto_bounds_ymin, __carto_bounds_ymax)
      FROM
        _main,
        _joined
      WHERE
          (_main.__carto_bounds_xmax >= _joined.__carto_bounds_xmin
          AND _main.__carto_bounds_xmin <= _joined.__carto_bounds_xmax
          AND _main.__carto_bounds_ymax >= _joined.__carto_bounds_ymin
          AND _main.__carto_bounds_ymin <= _joined.__carto_bounds_ymax)
        AND
          ${predicates.get(params.predicate as string)}(
            _joined.${params.geofilter}_processed,
            _main.${params.geosource}_processed
          )`
  }

  language(provider: Provider): Language {
    return Language.Python
  }

  supportedProviders(): Provider[] {
    return [Provider.DatabricksRest]
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  _getOutputSchemas({
    workflow,
    params,
    schemas,
    connection,
    componentName
  }: GetOutputSchemasParams): TableSchemasList {
    const tablename = params.source as string
    const columns = this._getSchemaForTablename(tablename, schemas, connection)
    const tablenameMatch = this._getDryRunOutputTableFqn(
      workflow,
      params,
      connection,
      componentName,
      FILTER_TABLE_TRUE_NAME
    )
    const tablenameUnmatch = this._getDryRunOutputTableFqn(
      workflow,
      params,
      connection,
      componentName,
      FILTER_TABLE_FALSE_NAME
    )
    return [
      {
        fqn: tablenameMatch,
        schema: columns
      },
      {
        fqn: tablenameUnmatch,
        schema: columns
      }
    ]
  }
}

export { SpatialFilterDB }
